<template>
  <v-card width="450" class="elevation-4 d-flex flex-column" style="max-height: 350px; min-height: 200px;">
    <v-toolbar flat dense color="grey lighten-4">
      <v-toolbar-title class="body-2 font-weight-bold">
        Comentários: {{ fieldName || fieldId }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasOpenComments"
        x-small
        color="success"
        outlined
        @click="resolveAll"
        :loading="resolving"
      >
        <v-icon left x-small>mdi-check</v-icon>
        Resolver
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-2 flex-grow-1 comment-list" ref="commentList" style="overflow-y: auto;">
      <div v-if="comments.length === 0" class="text-caption text-center grey--text py-4">
        Nenhum comentário neste campo.
      </div>
      
      <div v-for="c in comments" :key="c.id" class="mb-3">
        <div class="d-flex justify-space-between mb-1">
          <span class="caption font-weight-bold">{{ c.usuario.nome }}</span>
          <span class="caption grey--text">{{ formatDate(c.criado_em) }}</span>
        </div>
        <div class="body-2 px-2 py-1 rounded" :class="c.status === 'resolvido' ? 'grey lighten-4 grey--text' : 'blue lighten-5'">
          {{ c.mensagem }}
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="pa-2 grey lighten-5">
      <v-text-field
        v-model="newMessage"
        dense
        outlined
        hide-details
        placeholder="Escreva um comentário..."
        @keyup.enter="sendMessage"
        :loading="sending"
        :disabled="sending"
      >
        <template v-slot:append>
          <v-icon
            color="primary"
            @click="sendMessage"
            :disabled="!newMessage || sending"
            style="cursor: pointer"
          >
            mdi-send
          </v-icon>
        </template>
      </v-text-field>
    </v-card-actions>
  </v-card>
</template>

<script>
import ProjetoComentariosService from '@/services/projetos/projeto-comentarios.service';

export default {
  name: 'CommentPopover',
  props: {
    projectId: {
      type: [Number, String],
      required: true
    },
    fieldId: {
      type: String,
      required: true
    },
    fieldName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      newMessage: '',
      sending: false,
      resolving: false,
      resizeObserver: null
    };
  },
  mounted() {
    this.scrollToBottom();
    // Força o v-menu do Vuetify a recalcular sua posição se o v-card mudar de altura 
    // (ex: inserindo um comentário), permitindo que ele "vire" para cima caso bata no fim da tela.
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        window.dispatchEvent(new Event('resize'));
      });
      this.resizeObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  computed: {
    comments() {
      return this.$store.getters['projetos/getFieldComments'](this.fieldId) || [];
    },
    hasOpenComments() {
      return this.comments.some(c => c.status === 'aberto');
    },
    empresaId() {
      return this.$route.params.empresaId || this.$store.state.empresaId || 1; 
      // Em Teros, geralmente o empresaId vem da rota ou state local
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      this.sending = true;
      try {
        const result = await ProjetoComentariosService.createComentario(this.empresaId, this.projectId, {
          campo_identificador: this.fieldId,
          mensagem: this.newMessage
        });
        
        // Adiciona localmente na store via dispatch/commit
        this.$store.commit('projetos/ADD_FIELD_COMMENT', {
          fieldId: this.fieldId,
          comment: result.data
        });
        
        this.newMessage = '';

        this.$nextTick(() => {
          const list = this.$refs.commentList;
          if (list) {
            list.scrollTop = list.scrollHeight;
          }
        });
      } catch (err) {
        this.$notify({ group: 'geral', title: 'Erro', text: 'Não foi possível enviar o comentário', type: 'error' });
      } finally {
        this.sending = false;
      }
    },
    async resolveAll() {
      this.resolving = true;
      try {
        await ProjetoComentariosService.resolverComentarios(this.empresaId, this.projectId, this.fieldId);
        
        this.$store.commit('projetos/RESOLVE_FIELD_COMMENTS', this.fieldId);
        this.$notify({ group: 'geral', title: 'Sucesso', text: 'Comentários resolvidos', type: 'success' });
        this.$emit('close');
      } catch (err) {
        this.$notify({ group: 'geral', title: 'Erro', text: 'Não foi possível resolver os comentários', type: 'error' });
      } finally {
        this.resolving = false;
      }
    }
  }
};
</script>

<style scoped>
.comment-list::-webkit-scrollbar {
  width: 6px;
}
.comment-list::-webkit-scrollbar-track {
  background: transparent;
}
.comment-list::-webkit-scrollbar-thumb {
  background-color: #bdbdbd;
  border-radius: 4px;
}
</style>
